import { useContext } from 'react'

import { OptimizelyContext } from '@optimizely/react-sdk'
import Button from '@space-core/components/button'
import ModalFooter from '@space-core/components/modalFooter'
import useTranslation from '@space-core/hooks/useTranslation'
import { PixelPhase, PixelGroup, SuperPixelUtils } from '@space-core/utils/superPixel'
import useMembership from '@space-shell/common/hooks/useMembership'
import { useAuthStore } from '@space-shell/core/store'
import clsx from 'clsx'
import type { TFunction } from 'react-i18next'
import styled from 'styled-components'

const HotModalFooter = styled(ModalFooter)`
  border-top: 0.06rem solid var(--gray-200);
`

export type FooterProps = {
  id: string
  name: string
  price: string
  isMobile: boolean
  link: string
}

const renderButton = (link: string, t: TFunction, sendPixelEvent: () => void) => {
  const handleClick = () => {
    sendPixelEvent()
    window.open(link, '_blank')
  }

  return (
    <>
      <Button data-test='button-buy' onClick={handleClick} variation='success'>
        {t('bundle.products_sale')}
      </Button>
    </>
  )
}

const Footer = ({ id, name, price, isMobile, link }: FooterProps) => {
  const { t } = useTranslation()
  const { membershipSlug, membershipData } = useMembership()
  const { id: userId } = useAuthStore()
  const { optimizely } = useContext(OptimizelyContext)

  const sendPixelEvent = () => {
    if (isMobile) {
      optimizely?.track('click_buy_bundle')
    }
    SuperPixelUtils.sendEvent({
      ownerUcode: membershipData?.ownerUcode as string,
      instanceId: membershipSlug,
      group: PixelGroup.SalesEvents,
      phase: PixelPhase.ComboClickBuy,
      runtimeValues: {
        slug: membershipSlug,
        userId: userId,
        comboId: id,
        comboName: name,
        url: link,
        price
      }
    })
  }

  const footerClassName = clsx('_w-full _d-flex _px-5 _align-items-center _border-gray-200', {
    '_justify-content-end': !isMobile,
    '_flex-column ': isMobile
  })

  const dataTest = isMobile ? 'footer-price-mobile' : 'footer-price-desktop'

  return (
    <HotModalFooter className={footerClassName}>
      <span data-test={dataTest} className='_font-weight-bold _text-green _mr-4 _mb-3 _mb-md-0'>
        {price}
      </span>
      {renderButton(link, t, sendPixelEvent)}
    </HotModalFooter>
  )
}

export default Footer
